/**
 * Form Utilities for React Hook Form + Zod Integration
 *
 * This file provides custom hooks, helpers, and utilities for working with
 * React Hook Form and Zod validation in a type-safe manner.
 */

import { useForm, UseFormProps } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

/**
 * Custom hook that wraps useForm with Zod resolver
 * Provides automatic type inference from Zod schema
 *
 * In @hookform/resolvers v5+, types are automatically inferred from the schema
 *
 * @example
 * const schema = z.object({ name: z.string() });
 * const methods = useZodForm({ schema });
 */
export function useZodForm<TSchema extends z.ZodType>(
  props: Omit<UseFormProps, "resolver"> & {
    schema: TSchema;
  },
) {
  const { schema, ...formProps } = props;

  return useForm({
    ...formProps,
    resolver: zodResolver(schema as any),
  });
}
